import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Form, Row, Col, Card, Spinner } from 'react-bootstrap'
import 'bootstrap/dist/css/bootstrap.min.css'
import ApiEndpoints from './commonComponents/ApiEndpoints'
import toast from 'react-hot-toast'
import Cookies from 'js-cookie'
import {
  BsFunnelFill,
  BsSearch,
  BsArrowClockwise,
  BsCalendar3,
  BsFiletypePdf,
} from 'react-icons/bs'
import { LuGraduationCap } from 'react-icons/lu'
import { Typeahead } from 'react-bootstrap-typeahead'
import { Table, Tbody, Td, Th, Thead, Tr } from 'react-super-responsive-table'
import html2pdf from 'html2pdf.js'
import { useAuth } from './AuthContext'
import { useSidebar } from './SidebarContext'
import ProfileDropdown from './commonComponents/ProfileDropdown'
import { useInstitute } from './commonComponents/InstituteContext'
import ExpandableReasonText from './commonComponents/ExpandableReasonText'

const formatApiDate = (date) => {
  if (!date || !(date instanceof Date) || Number.isNaN(date.getTime())) {
    return null
  }
  return date.toISOString().split('T')[0]
}

const normalizeApiDate = (date) => {
  if (!date) return ''

  const parsedDate = new Date(date)
  if (Number.isNaN(parsedDate.getTime())) {
    return String(date).split('T')[0]
  }

  return parsedDate.toISOString().split('T')[0]
}

function blobToDataURL(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onloadend = () => resolve(reader.result)
    reader.onerror = reject
    reader.readAsDataURL(blob)
  })
}

async function fetchImageAsDataUrlForPdf(url) {
  if (!url || String(url).startsWith('data:')) return url

  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit' })
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    // Direct cross-origin fetch failed; try proxies below.
  }

  try {
    const proxiedUrl = `https://images.weserv.nl/?url=${encodeURIComponent(url)}`
    const res = await fetch(proxiedUrl)
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    // Try one more proxy.
  }

  try {
    const proxiedUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`
    const res = await fetch(proxiedUrl)
    if (res.ok) {
      return await blobToDataURL(await res.blob())
    }
  } catch {
    // Ignore and fall through.
  }

  return null
}

async function makeWatermarkImageDataUrl(sourceDataUrl, size = 600, opacity = 0.08) {
  if (!sourceDataUrl) return null

  try {
    return await new Promise((resolve, reject) => {
      const imageEl = new Image()
      imageEl.onload = () => {
        try {
          const canvas = document.createElement('canvas')
          canvas.width = size
          canvas.height = size
          const ctx = canvas.getContext('2d')
          const minSide = Math.min(imageEl.naturalWidth, imageEl.naturalHeight)
          const sx = (imageEl.naturalWidth - minSide) / 2
          const sy = (imageEl.naturalHeight - minSide) / 2

          ctx.clearRect(0, 0, size, size)
          ctx.save()
          ctx.beginPath()
          ctx.arc(size / 2, size / 2, size / 2, 0, Math.PI * 2)
          ctx.closePath()
          ctx.clip()
          ctx.globalAlpha = opacity
          ctx.drawImage(imageEl, sx, sy, minSide, minSide, 0, 0, size, size)
          ctx.restore()

          resolve(canvas.toDataURL('image/png'))
        } catch (e) {
          reject(e)
        }
      }
      imageEl.onerror = reject
      imageEl.src = sourceDataUrl
    })
  } catch {
    return sourceDataUrl
  }
}

const formatDisplayDate = (date) => {
  if (!date) return 'N/A'

  const parsedDate = new Date(date)
  if (Number.isNaN(parsedDate.getTime())) return 'N/A'

  return parsedDate.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}

const formatAttendanceStatus = (status) => {
  if (!status) return 'N/A'

  return String(status)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase())
}

const getSessionName = () => Cookies.get('session_name') || Cookies.get('session') || ''

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

const getHolidayListFromResponse = (responseData) => {
  if (Array.isArray(responseData?.data)) return responseData.data
  if (responseData?.data && typeof responseData.data === 'object') {
    return [responseData.data]
  }
  if (Array.isArray(responseData?.holidays)) return responseData.holidays
  if (Array.isArray(responseData?.holiday)) return responseData.holiday
  if (responseData?.holiday && typeof responseData.holiday === 'object') {
    return [responseData.holiday]
  }
  return []
}

const isStaffHoliday = (holiday) => {
  const holidayType = String(
    holiday?.holiday_type || holiday?.type || '',
  ).toUpperCase()
  return !holidayType || holidayType === 'ALL' || holidayType === 'STAFF'
}

const mapAttendanceRow = (
  att,
  teachersById = {},
  holidayDates = new Set(),
  holidayTitleByDate = {},
  topLevelHolidayTitle = '',
) => {
  const teacher = teachersById[Number(att.teacher_id)]
  const date = normalizeApiDate(att.date)
  const isHoliday = holidayDates.has(date)
  const status = isHoliday ? 'holiday' : att.status
  const isHolidayStatus = String(status || '').toLowerCase() === 'holiday'

  let remarks = att.remarks
  if (isHolidayStatus) {
    remarks =
      holidayTitleByDate[date] ||
      topLevelHolidayTitle ||
      att.holiday_title ||
      att.remarks
  }

  return {
    id: att.id,
    teacher_id: att.teacher_id,
    teacher_name: att.teacher_name || teacher?.name || '-',
    email: teacher?.email || '-',
    department: teacher?.qualification || teacher?.department || '-',
    date: date || att.date,
    status,
    remarks,
  }
}

const getDateRange = (fromDate, toDate) => {
  const start = formatApiDate(fromDate)
  const end = formatApiDate(toDate)

  if (start && end) {
    return start <= end ? { start, end } : { start: end, end: start }
  }

  if (start || end) {
    const date = start || end
    return { start: date, end: date }
  }

  return null
}

const isDateInRange = (date, range) =>
  !range || (date >= range.start && date <= range.end)

const addMissingHolidayRows = (
  rows,
  holidays,
  teachers,
  selectedTeacherId,
  dateRange,
) => {
  const targetTeachers = selectedTeacherId
    ? teachers.filter((teacher) => Number(teacher.id) === Number(selectedTeacherId))
    : teachers
  const existingRows = new Set(
    rows.map(
      (row) => `${Number(row.teacher_id)}-${normalizeApiDate(row.date)}`,
    ),
  )
  const applicableHolidays = holidays
    .filter(isStaffHoliday)
    .filter((h) => {
      const d = normalizeApiDate(h?.date)
      return d && isDateInRange(d, dateRange)
    })
  const holidayTitleByDate = {}
  applicableHolidays.forEach((h) => {
    const d = normalizeApiDate(h?.date)
    if (d && !holidayTitleByDate[d]) holidayTitleByDate[d] = h.title || ''
  })
  const holidayDates = Array.from(new Set(Object.keys(holidayTitleByDate)))
  const missingRows = []

  holidayDates.forEach((date) => {
    targetTeachers.forEach((teacher) => {
      const rowKey = `${Number(teacher.id)}-${date}`
      if (existingRows.has(rowKey)) return

      existingRows.add(rowKey)
      missingRows.push({
        id: `holiday-${teacher.id}-${date}`,
        teacher_id: teacher.id,
        teacher_name: teacher.name || '-',
        email: teacher.email || '-',
        department: teacher.qualification || teacher.department || '-',
        date,
        status: 'holiday',
        remarks: holidayTitleByDate[date] || 'Holiday',
      })
    })
  })

  return [...rows, ...missingRows]
}

export default function ViewTeacherAttendance() {
  const { logout } = useAuth()
  const { isCollapsed } = useSidebar()
  const institute_id = Cookies.get('institute_id')
  const tablePdfRef = useRef(null)
  const [marginLeft, setMarginLeft] = useState('0px')
  const [isMobile, setIsMobile] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false,
  )
  const [logoError, setLogoError] = useState(false)
  const { logo, name } = useInstitute()
  const [showFilterCard, setShowFilterCard] = useState(true)
  const [openTable, setOpenTable] = useState(false)
  const [allTeachers, setAllTeachers] = useState([])
  const [selectedTeacherId, setSelectedTeacherId] = useState(null)
  const [selectedTeacher, setSelectedTeacher] = useState(null)
  const [attendanceData, setAttendanceData] = useState([])
  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)
  const [attendanceStatusFilter, setAttendanceStatusFilter] = useState('all')
  const [loadingTeachers, setLoadingTeachers] = useState(false)
  const [loadingAttendance, setLoadingAttendance] = useState(false)
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        const expandedWidth = 245
        const collapsedWidth = 85
        const gap = 1
        setMarginLeft(
          isCollapsed
            ? `${collapsedWidth + gap}px`
            : `${expandedWidth + gap}px`,
        )
      } else {
        setMarginLeft('0px')
      }
      setIsMobile(window.innerWidth <= 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [isCollapsed])

  const teachersById = useCallback(() => {
    const map = {}
    allTeachers.forEach((t) => {
      map[Number(t.id)] = t
    })
    return map
  }, [allTeachers])

  const fetchTeachers = useCallback(async () => {
    if (!institute_id) {
      toast.error('Institute not selected. Please log in again.')
      return []
    }
    setLoadingTeachers(true)
    try {
      const res = await ApiEndpoints.getAllTeachers({
        'institute-Id': Number(institute_id),
      })
      const teachers = (res.data?.data || []).filter(
        (t) => String(t.status || '').toLowerCase() === 'active',
      )
      setAllTeachers(teachers)
      return teachers
    } catch (error) {
      toast.error(
        error.response?.data?.message || 'Failed to load teachers.',
      )
      setAllTeachers([])
      return []
    } finally {
      setLoadingTeachers(false)
    }
  }, [institute_id])

  const buildAttendancePayload = useCallback(() => {
    const payload = {}
    if (selectedTeacherId) {
      payload.teacher_id = Number(selectedTeacherId)
    }

    const start = formatApiDate(fromDate)
    const end = formatApiDate(toDate)

    if (start && end) {
      payload.start_date = start
      payload.end_date = end
    } else if (start) {
      payload.date = start
    } else if (end) {
      payload.date = end
    }

    if (attendanceStatusFilter !== 'all' && attendanceStatusFilter !== 'holiday') {
      payload.status = attendanceStatusFilter
    }

    return payload
  }, [selectedTeacherId, fromDate, toDate, attendanceStatusFilter])

  const fetchAttendance = useCallback(async () => {
    if (!institute_id) return

    setLoadingAttendance(true)
    try {
      const payload = buildAttendancePayload()
      const session = getSessionName()
      const [attendanceRes, holidayRes] = await Promise.all([
        ApiEndpoints.getTeacherAttendance(payload),
        session
          ? ApiEndpoints.getHolidayList(session, 'ALL').catch(() => null)
          : Promise.resolve(null),
      ])
      const list = attendanceRes.data?.attendance || []
      const topLevelHolidayTitle = attendanceRes.data?.holiday_title || ''
      const holidays = getHolidayListFromResponse(holidayRes?.data)
      const holidayTitleByDate = {}
      holidays.filter(isStaffHoliday).forEach((h) => {
        const d = normalizeApiDate(h?.date)
        if (d && !holidayTitleByDate[d]) holidayTitleByDate[d] = h.title || ''
      })
      const holidayDates = new Set(Object.keys(holidayTitleByDate))
      const byId = teachersById()
      const dateRange = getDateRange(fromDate, toDate)
      const rowsWithHolidays =
        attendanceStatusFilter === 'all' || attendanceStatusFilter === 'holiday'
          ? addMissingHolidayRows(
              list.map((att) =>
                mapAttendanceRow(
                  att,
                  byId,
                  holidayDates,
                  holidayTitleByDate,
                  topLevelHolidayTitle,
                ),
              ),
              holidays,
              allTeachers,
              selectedTeacherId,
              dateRange,
            )
          : list.map((att) =>
              mapAttendanceRow(
                att,
                byId,
                holidayDates,
                holidayTitleByDate,
                topLevelHolidayTitle,
              ),
            )
      const filteredRows =
        attendanceStatusFilter === 'all'
          ? rowsWithHolidays
          : rowsWithHolidays.filter(
              (row) =>
                String(row.status || '').toLowerCase() === attendanceStatusFilter,
            )

      setAttendanceData(filteredRows)
      setOpenTable(true)
    } catch (error) {
      toast.error(
        error.response?.data?.message ||
          'Failed to load teacher attendance.',
      )
      setAttendanceData([])
      setOpenTable(true)
    } finally {
      setLoadingAttendance(false)
    }
  }, [
    institute_id,
    buildAttendancePayload,
    teachersById,
    fromDate,
    toDate,
    attendanceStatusFilter,
    allTeachers,
    selectedTeacherId,
  ])

  useEffect(() => {
    fetchTeachers()
  }, [fetchTeachers])

  useEffect(() => {
    if (!institute_id) return
    fetchAttendance()
  }, [fetchAttendance, institute_id])

  const handleTeacherChange = (teacher) => {
    if (teacher) {
      setSelectedTeacher(teacher)
      setSelectedTeacherId(teacher.id)
    } else {
      setSelectedTeacher(null)
      setSelectedTeacherId(null)
    }
  }

  const handleRefresh = async () => {
    setSelectedTeacher(null)
    setSelectedTeacherId(null)
    setFromDate(null)
    setToDate(null)
    setAttendanceStatusFilter('all')
    await fetchTeachers()
  }

  const isLoading = loadingTeachers || loadingAttendance
  const isPdfDisabled =
    !openTable || isGeneratingPdf || isLoading || attendanceData.length === 0

  const getStatusColor = (status) => {
    const s = String(status || '').toLowerCase()
    if (s === 'present') return '#10B981'
    if (s === 'absent') return '#EF4444'
    if (s === 'holiday') return '#F59E0B'
    if (s === 'leave') return '#F59E0B'
    if (s === 'half_day') return '#3B82F6'
    if (s === 'late') return '#8B5CF6'
    return '#111827'
  }

  const getRowBgColor = (status, index) => {
    const s = String(status || '').toLowerCase()
    if (s === 'present') return 'rgba(16, 185, 129, 0.1)'
    if (s === 'absent') return 'rgba(239, 68, 68, 0.1)'
    if (s === 'holiday') return 'rgba(245, 158, 11, 0.1)'
    return index % 2 === 0 ? '#ffffff' : 'rgba(243, 244, 246, 0.4)'
  }

  const handleDownloadPdf = async () => {
    if (!tablePdfRef.current || !openTable) {
      toast.error('No teacher attendance table available to download.')
      return
    }

    if (!attendanceData || attendanceData.length === 0) {
      toast.error('No teacher attendance data available to download.')
      return
    }

    setIsGeneratingPdf(true)
    let pdfWrapper = null

    try {
      const instituteName =
        name || Cookies.get('name') || Cookies.get('institute_name') || 'Institute'
      const selectedTeacherName = selectedTeacher?.name || 'All'
      const filenameTeacherName = selectedTeacherName.trim().replace(/\s+/g, '_')
      const filename = `${filenameTeacherName}_teacher_attendance_${new Date()
        .toISOString()
        .split('T')[0]}.pdf`
      const instituteLogoUrl =
        Cookies.get('institute_logo') ||
        (logo && String(logo).trim() !== '' && String(logo).toLowerCase() !== 'no_image.png'
          ? logo
          : '')
      let instituteWatermarkData = null

      if (instituteLogoUrl) {
        try {
          const instituteLogoRawData = await fetchImageAsDataUrlForPdf(instituteLogoUrl)
          instituteWatermarkData = await makeWatermarkImageDataUrl(instituteLogoRawData)
        } catch {
          instituteWatermarkData = null
        }
      }

      const options = {
        margin: [10, 10, 10, 10],
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'landscape' },
        pagebreak: { mode: ['css', 'legacy'] },
      }

      pdfWrapper = document.createElement('div')
      pdfWrapper.style.position = 'fixed'
      pdfWrapper.style.left = '-9999px'
      pdfWrapper.style.top = '0'
      pdfWrapper.style.width = '1120px'
      pdfWrapper.style.padding = '16px'
      pdfWrapper.style.backgroundColor = '#ffffff'
      pdfWrapper.style.fontFamily = 'Inter, Arial, sans-serif'

      const printableContent = document.createElement('div')
      printableContent.style.position = 'relative'
      printableContent.style.backgroundColor = '#ffffff'
      printableContent.style.padding = '8px'

      const contentWrapper = document.createElement('div')
      contentWrapper.style.position = 'relative'
      contentWrapper.style.zIndex = '2'

      const instituteHeading = document.createElement('h2')
      instituteHeading.textContent = instituteName
      instituteHeading.style.margin = '0 0 8px 0'
      instituteHeading.style.textAlign = 'center'
      instituteHeading.style.fontSize = '24px'
      instituteHeading.style.fontWeight = '700'
      instituteHeading.style.color = '#111827'

      const reportHeading = document.createElement('h3')
      reportHeading.textContent = 'Attendance Report'
      reportHeading.style.margin = '0 0 12px 0'
      reportHeading.style.textAlign = 'center'
      reportHeading.style.fontSize = '18px'
      reportHeading.style.fontWeight = '700'
      reportHeading.style.color = '#223843'

      const tableContainer = document.createElement('div')
      tableContainer.style.position = 'relative'
      tableContainer.style.minHeight = '320px'

      if (instituteWatermarkData) {
        const watermarkImage = document.createElement('img')
        watermarkImage.src = instituteWatermarkData
        watermarkImage.alt = 'Institute watermark'
        watermarkImage.style.position = 'absolute'
        watermarkImage.style.top = '55%'
        watermarkImage.style.left = '50%'
        watermarkImage.style.transform = 'translate(-50%, -50%)'
        watermarkImage.style.width = '320px'
        watermarkImage.style.height = '320px'
        watermarkImage.style.objectFit = 'cover'
        watermarkImage.style.borderRadius = '50%'
        watermarkImage.style.opacity = '0.18'
        watermarkImage.style.pointerEvents = 'none'
        watermarkImage.style.zIndex = '1'
        tableContainer.appendChild(watermarkImage)
      }

      const printableTable = document.createElement('table')
      printableTable.style.width = '100%'
      printableTable.style.borderCollapse = 'collapse'
      printableTable.style.position = 'relative'
      printableTable.style.zIndex = '2'
      printableTable.style.backgroundColor = 'rgba(255, 255, 255, 0.92)'

      printableTable.innerHTML = `
        <thead>
          <tr style="background-color: #F3F4F6;">
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Teacher Name</th>
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Email</th>
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Date</th>
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Status</th>
            <th style="padding: 10px; border: 1px solid #D1D3D4; text-align: left; font-size: 12px;">Remarks</th>
          </tr>
        </thead>
        <tbody>
          ${attendanceData
            .map(
              (att) => `
                <tr>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px; font-weight: 700;">${escapeHtml(att.teacher_name || '-')}</td>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px;">${escapeHtml(att.email || '-')}</td>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px;">${escapeHtml(formatDisplayDate(att.date))}</td>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px; font-weight: 700;">${escapeHtml(formatAttendanceStatus(att.status))}</td>
                  <td style="padding: 9px; border: 1px solid #E5E7EB; font-size: 12px;">${escapeHtml(att.remarks || '-')}</td>
                </tr>
              `,
            )
            .join('')}
        </tbody>
      `

      tableContainer.appendChild(printableTable)
      contentWrapper.appendChild(instituteHeading)
      contentWrapper.appendChild(reportHeading)
      contentWrapper.appendChild(tableContainer)
      printableContent.appendChild(contentWrapper)
      pdfWrapper.appendChild(printableContent)
      document.body.appendChild(pdfWrapper)

      await html2pdf().set(options).from(printableContent).save()
    } catch {
      toast.error('Failed to generate PDF. Please try again.')
    } finally {
      if (pdfWrapper?.parentNode) {
        pdfWrapper.parentNode.removeChild(pdfWrapper)
      }
      setIsGeneratingPdf(false)
    }
  }

  return (
    <>
      <style>
        {`
          .filter-card-container {
            overflow: visible !important;
            position: relative !important;
            z-index: 10 !important;
          }
          .table td {
            user-select: text;
          }
          .table tbody tr {
            cursor: default;
          }
        `}
      </style>
      <div
        style={{
          marginLeft,
          transition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          willChange: 'margin-left',
          WebkitTransition: 'margin-left 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
          position: 'relative',
          zIndex: 1,
          marginTop: '0px',
          paddingLeft: isCollapsed ? '12px' : '12px',
          paddingRight: isCollapsed ? '12px' : '12px',
        }}
      >
        <div
          className="container-fluid p-0"
          style={{
            marginTop: window.innerWidth < 992 ? '55px' : '-12px',
            paddingLeft: '0px',
            paddingRight: '0px',
          }}
        >
          <div style={{ marginLeft: isMobile ? '-13px' : '-40px' }}>
            <div
              className="d-flex justify-content-between align-items-center"
              style={{
                background: '#F3F4F6',
                paddingLeft: '28px',
                paddingRight: '28px',
                paddingTop: '12px',
                paddingBottom: '12px',
                overflow: 'visible',
                borderBottom: '1px solid #D1D3D4',
                width: '100%',
              }}
            >
              <div className="d-flex align-items-center gap-3">
                <div
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    overflow: 'hidden',
                    background: '#223843',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {!logoError ? (
                    <img
                      src={logo}
                      alt="School Logo"
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={() => setLogoError(true)}
                    />
                  ) : (
                    <LuGraduationCap size={20} style={{ color: '#ffffff' }} />
                  )}
                </div>
                <h3
                  className="m-0 fw-bold"
                  style={{
                    letterSpacing: '-0.02em',
                    overflow: 'visible',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    fontSize: 'clamp(18px, 2.5vw, 22px)',
                    color: '#223843',
                    lineHeight: '1.2',
                    fontFamily: 'Inter, system-ui, sans-serif',
                  }}
                >
                  {selectedTeacher ? (
                    <>
                      View{' '}
                      <span style={{ color: '#223843' }}>
                        {selectedTeacher.name.charAt(0).toUpperCase() +
                          selectedTeacher.name.slice(1).toLowerCase()}
                      </span>
                      &apos;s Attendance
                    </>
                  ) : (
                    'View Teacher Attendance'
                  )}
                </h3>
              </div>
              {!isMobile && <ProfileDropdown logout={logout} />}
            </div>
          </div>
        </div>

        <div
          className="container-fluid p-0 mt-4"
          style={{
            minHeight: '100vh',
            paddingLeft: isCollapsed ? '32px' : '40px',
            paddingRight: '32px',
            paddingTop: '32px',
            paddingBottom: '32px',
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex align-items-center gap-3">
              <button
                onClick={() => setShowFilterCard(!showFilterCard)}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '50%',
                  backgroundColor: showFilterCard ? '#223843' : '#ffffff',
                  border: '1px solid #D1D3D4',
                  color: showFilterCard ? '#ffffff' : '#223843',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                }}
                title={showFilterCard ? 'Hide Filters' : 'Show Filters'}
              >
                <BsFunnelFill size={20} />
              </button>
            </div>
          </div>

          <div
            className="filter-card-container"
            style={{
              maxHeight: showFilterCard ? '1000px' : '0',
              overflow: showFilterCard ? 'visible' : 'hidden',
              transition: 'max-height 0.3s ease-in-out',
              marginBottom: showFilterCard ? '24px' : '0',
              position: 'relative',
              zIndex: showFilterCard ? 10 : 1,
            }}
          >
            {showFilterCard && (
              <Card
                style={{
                  border: '1px solid #D1D3D4',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  overflow: 'visible',
                  position: 'relative',
                }}
              >
                <div
                  style={{
                    background: '#ffffff',
                    padding: '20px',
                    borderBottom: '1px solid #F3F4F6',
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-3">
                      <div
                        style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '50%',
                          backgroundColor: '#223843',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                        }}
                      >
                        <BsSearch style={{ color: '#ffffff', fontSize: '18px' }} />
                      </div>
                      <div>
                        <h5
                          className="m-0 fw-semibold text-start"
                          style={{
                            color: '#111827',
                            fontSize: '16px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Select Teacher
                        </h5>
                        <p
                          className="m-0 mt-1"
                          style={{ fontSize: '13px', color: '#6b7280' }}
                        >
                          Choose a teacher to view attendance records
                        </p>
                      </div>
                    </div>
                    <div className="d-flex gap-2">
                      <button
                        onClick={handleDownloadPdf}
                        disabled={isPdfDisabled}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: isPdfDisabled ? '#9CA3AF' : '#2563EB',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: isPdfDisabled ? 'not-allowed' : 'pointer',
                          opacity: isGeneratingPdf ? 0.7 : 1,
                        }}
                        title={
                          isGeneratingPdf
                            ? 'Generating PDF...'
                            : 'Download Teacher Attendance PDF'
                        }
                      >
                        <BsFiletypePdf size={18} />
                      </button>
                      <button
                        onClick={handleRefresh}
                        disabled={isLoading}
                        style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '50%',
                          backgroundColor: '#10B981',
                          border: 'none',
                          color: '#ffffff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: isLoading ? 'not-allowed' : 'pointer',
                          opacity: isLoading ? 0.6 : 1,
                        }}
                        title="Refresh"
                      >
                        <BsArrowClockwise size={20} />
                      </button>
                    </div>
                  </div>
                </div>

                <Card.Body
                  className="p-4"
                  style={{
                    backgroundColor: '#ffffff',
                    overflow: 'visible',
                    position: 'relative',
                  }}
                >
                  <Row className="g-3 text-start">
                    <Col xs={12} md={6} lg={4}>
                      <div style={{ position: 'relative' }}>
                        <label
                          className="fw-semibold mb-2 d-block"
                          style={{
                            color: '#6b7280',
                            fontSize: '13px',
                            fontFamily: 'Inter, system-ui, sans-serif',
                          }}
                        >
                          Select Teacher
                        </label>
                        <Typeahead
                          id="view-teacher-select"
                          labelKey={(option) =>
                            `${option.name}`
                          }
                          onChange={(selected) => {
                            handleTeacherChange(selected[0] || null)
                          }}
                          options={allTeachers}
                          placeholder={
                            loadingTeachers
                              ? 'Loading teachers...'
                              : 'Select Teacher (optional)'
                          }
                          disabled={loadingTeachers}
                          selected={
                            selectedTeacher
                              ? allTeachers.filter(
                                  (t) => t.id === selectedTeacher.id,
                                )
                              : []
                          }
                          inputProps={{
                            style: {
                              borderRadius: '8px',
                              border: '1px solid #D1D3D4',
                              fontSize: '14px',
                              height: '40px',
                              backgroundColor: '#ffffff',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            },
                          }}
                        />
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            )}
          </div>

          {openTable && (
            <Row className="mb-3 d-flex justify-content-start gap-3 text-start">
              <Col xs={12} md={3}>
                <div style={{ position: 'relative' }}>
                  <label
                    className="fw-semibold mb-2 d-block"
                    style={{
                      color: '#6b7280',
                      fontSize: '13px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Start Date
                  </label>
                  <BsCalendar3
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      marginTop: '12px',
                      color: '#6b7280',
                      fontSize: '16px',
                      zIndex: 1,
                      pointerEvents: 'none',
                    }}
                  />
                  <Form.Control
                    type="date"
                    value={
                      fromDate instanceof Date && !Number.isNaN(fromDate)
                        ? fromDate.toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setFromDate(e.target.value ? new Date(e.target.value) : null)
                    }
                    disabled={loadingAttendance}
                    style={{
                      paddingLeft: '40px',
                      borderRadius: '8px',
                      border: '1px solid #D1D3D4',
                      fontSize: '14px',
                      height: '40px',
                      backgroundColor: '#ffffff',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  />
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div style={{ position: 'relative' }}>
                  <label
                    className="fw-semibold mb-2 d-block"
                    style={{
                      color: '#6b7280',
                      fontSize: '13px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    End Date
                  </label>
                  <BsCalendar3
                    style={{
                      position: 'absolute',
                      left: '12px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      marginTop: '12px',
                      color: '#6b7280',
                      fontSize: '16px',
                      zIndex: 1,
                      pointerEvents: 'none',
                    }}
                  />
                  <Form.Control
                    type="date"
                    value={
                      toDate instanceof Date && !Number.isNaN(toDate)
                        ? toDate.toISOString().split('T')[0]
                        : ''
                    }
                    onChange={(e) =>
                      setToDate(e.target.value ? new Date(e.target.value) : null)
                    }
                    disabled={loadingAttendance}
                    style={{
                      paddingLeft: '40px',
                      borderRadius: '8px',
                      border: '1px solid #D1D3D4',
                      fontSize: '14px',
                      height: '40px',
                      backgroundColor: '#ffffff',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  />
                </div>
              </Col>
              <Col xs={12} md={3}>
                <div>
                  <label
                    className="fw-semibold mb-2 d-block"
                    style={{
                      color: '#6b7280',
                      fontSize: '13px',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    Attendance Status
                  </label>
                  <Form.Select
                    value={attendanceStatusFilter}
                    onChange={(e) => setAttendanceStatusFilter(e.target.value)}
                    disabled={loadingAttendance}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #D1D3D4',
                      fontSize: '14px',
                      height: '40px',
                      backgroundColor: '#ffffff',
                      fontFamily: 'Inter, system-ui, sans-serif',
                    }}
                  >
                    <option value="all">All</option>
                    <option value="present">Present</option>
                    <option value="absent">Absent</option>
                    <option value="leave">Leave</option>
                    <option value="half_day">Half Day</option>
                    <option value="late">Late</option>
                    <option value="holiday">Holiday</option>
                  </Form.Select>
                </div>
              </Col>
            </Row>
          )}

          {isLoading && (
            <div className="text-center py-5">
              <Spinner animation="border" style={{ color: '#223843' }} />
              <p
                className="mt-3"
                style={{
                  color: '#6b7280',
                  fontSize: '14px',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
              >
                Loading teacher attendance...
              </p>
            </div>
          )}

          {!isLoading && openTable && (
            <Card
              ref={tablePdfRef}
              style={{
                border: '1px solid #D1D3D4',
                borderRadius: '12px',
                overflow: 'hidden',
                backgroundColor: '#ffffff',
              }}
            >
              <Table
                className="table mb-0"
                style={{ borderRadius: '12px', overflow: 'hidden' }}
              >
                <Thead>
                  <Tr style={{ backgroundColor: '#F3F4F6' }}>
                    <Th
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        padding: '16px',
                        border: 'none',
                        borderBottom: '1px solid #D1D3D4',
                        textAlign: 'left',
                        color: '#111827',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Teacher Name
                    </Th>
                    <Th
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        padding: '16px',
                        border: 'none',
                        borderBottom: '1px solid #D1D3D4',
                        textAlign: 'left',
                        color: '#111827',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Email
                    </Th>
                    <Th
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        padding: '16px',
                        border: 'none',
                        borderBottom: '1px solid #D1D3D4',
                        textAlign: 'left',
                        color: '#111827',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Date
                    </Th>
                    <Th
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        padding: '16px',
                        border: 'none',
                        borderBottom: '1px solid #D1D3D4',
                        textAlign: 'left',
                        color: '#111827',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Status
                    </Th>
                    <Th
                      style={{
                        fontSize: '13px',
                        fontWeight: '600',
                        padding: '16px',
                        border: 'none',
                        borderBottom: '1px solid #D1D3D4',
                        textAlign: 'left',
                        color: '#111827',
                        fontFamily: 'Inter, system-ui, sans-serif',
                      }}
                    >
                      Remarks
                    </Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {attendanceData.length > 0 ? (
                    attendanceData.map((att, index) => {
                      const baseBgColor = getRowBgColor(att.status, index)
                      const statusColor = getStatusColor(att.status)

                      return (
                        <Tr
                          key={att.id || `${att.teacher_id}-${att.date}-${index}`}
                          style={{
                            backgroundColor: baseBgColor,
                            transition: 'background-color 0.2s ease',
                            borderBottom: '1px solid #E5E7EB',
                          }}
                        >
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#223843',
                              fontWeight: '600',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {att.teacher_name}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {att.email}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {att.date
                              ? new Date(att.date).toLocaleDateString('en-GB', {
                                  day: '2-digit',
                                  month: 'long',
                                  year: 'numeric',
                                })
                              : 'N/A'}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: statusColor,
                              fontWeight: '600',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                            }}
                          >
                            {formatAttendanceStatus(att.status)}
                          </Td>
                          <Td
                            style={{
                              fontSize: '14px',
                              padding: '16px',
                              border: 'none',
                              color: '#111827',
                              textAlign: 'left',
                              fontFamily: 'Inter, system-ui, sans-serif',
                              minWidth: isMobile ? 'auto' : '180px',
                              maxWidth: isMobile ? '100%' : '340px',
                              verticalAlign: 'top',
                            }}
                          >
                            <ExpandableReasonText
                              text={att.remarks || '—'}
                              isMobile={isMobile}
                              charLimit={30}
                              contentLabel="Remarks"
                              modalTitle="Attendance Details"
                              modalDetails={[
                                { label: 'Teacher Name', value: att.teacher_name || '—' },
                                { label: 'Email', value: att.email || '—' },
                                { label: 'Date', value: formatDisplayDate(att.date) },
                                {
                                  label: 'Status',
                                  value: formatAttendanceStatus(att.status),
                                },
                              ]}
                            />
                          </Td>
                        </Tr>
                      )
                    })
                  ) : (
                    <Tr>
                      <Td
                        colSpan="5"
                        className="text-center"
                        style={{
                          padding: '40px',
                          fontSize: '14px',
                          color: '#6b7280',
                          fontFamily: 'Inter, system-ui, sans-serif',
                        }}
                      >
                        No attendance data found.
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}
